import type { ReactNode } from 'react';
import {
  DesignerBadge,
  EmptyState,
  LoadingState,
  MetaCard,
  MetaStrip,
  MetricBand,
  MetricCard as DesignerMetricCard,
  MetricRing,
  PageHeader,
  RecordCard,
  RecordRow,
  StatusChip,
  Table,
  Td,
  Th,
  Tr,
  type BadgeTone,
  type IconName,
  type RingSegment,
} from '@tessma/design-system';
import { Refusal, formatDay, formatMoney, useAsync } from '../ui/async.js';
import {
  billStatusTone,
  billStatusWord,
  invoiceStatusTone,
  invoiceStatusWord,
} from './lifecycle.js';
import type { MetricCard } from '../analytics/gateway.js';
import type { AnalyticsGateway } from '../analytics/gateway.js';
import type { PartiesGateway } from '../parties/gateway.js';
import type { Bill, EntitySettings, FinanceGateway, Invoice } from './gateway.js';

/**
 * FINANCE OVERVIEW — Screen 01, converted from the Stitch markup.
 *
 * **THE STRUCTURE IS THE REFERENCE'S**, not an equivalent of it:
 * `01_Tenant_App/Finance/01_Finance_Overview/code.html` draws a page banner, a
 * four-card meta strip, four KPI cards (one of them a status breakdown with a
 * donut), the module's functions in five columns, and the two most recent
 * record sets side by side. Each of those is a component in
 * `@tessma/design-system/overview`, converted element by element, and this file
 * binds them to the authorised Finance data.
 *
 * WHAT DID NOT CHANGE, AND MUST NOT. The figures are the platform's metric
 * cards, filtered by the server: a tenant without Finance sees no Finance card,
 * and a caller who may not see a figure is told so in words rather than shown a
 * zero. Nothing here counts, sums, filters or hides. The links are the module's
 * own, offered on the permission each route and API already guard on.
 *
 * **A REFUSED CARD IS NOT ZERO.** Zero is a data claim — "there are none of
 * these" — and a caller who may not see a figure has been told nothing about
 * whether it is large or small.
 */

/* ---------------------------------------------------------------------------
 * Presentation tables. Each decides what a reader SEES beside a figure and
 * nothing else: the key, the title, the unit and the permission are the
 * server's, and a metric missing from a table below still renders.
 * ------------------------------------------------------------------------ */

/**
 * THE FOUR CARDS THIS SCREEN HAS, IN THE REFERENCE'S ORDER.
 *
 * **A LIST, NOT A FILTER.** It named an order and every other Finance metric
 * followed the four in the same grid, which put revenue, expenses and net
 * profit in a second row the reference does not draw. The composition is the
 * markup's, not the metric catalogue's: a module declaring an eighth metric
 * tomorrow must not add an eighth card to a screen designed for four. The
 * figures that are not here are not hidden — revenue, expenses and net profit
 * lead Screen 00's executive row, and every one of them has a report behind it.
 *
 * Stitch leads Screen 01 with the operational position — what is owed to you,
 * what state the invoices are in, what you owe, what is waiting on somebody.
 * Screen 00 leads with the business position instead; both orders are
 * deliberate, and each screen states its own.
 */
const SCREEN_CARDS: readonly string[] = [
  'finance.outstanding_receivables',
  'finance.invoices_by_status',
  'finance.outstanding_payables',
  'finance.bills_awaiting_approval',
];

/*
 * THE GLYPHS ARE THE DESIGNER'S OWN, per card: `Coins` on receivables,
 * `FileText` on the status split, `CreditCard` on payables, `Receipt` on what
 * is waiting for somebody. The tones are the designer's four grounds — green,
 * blue, red, amber — by frozen token name rather than by hex.
 */
const METRIC_STYLE: Record<string, { readonly icon: IconName; readonly tone: BadgeTone }> = {
  'finance.outstanding_receivables': { icon: 'coins', tone: 'success' },
  'finance.invoices_by_status': { icon: 'file-text', tone: 'info' },
  'finance.outstanding_payables': { icon: 'credit-card', tone: 'danger' },
  'finance.bills_awaiting_approval': { icon: 'receipt', tone: 'warning' },
  'finance.revenue': { icon: 'trend-up', tone: 'success' },
  'finance.expenses': { icon: 'trend-down', tone: 'danger' },
  'finance.net_profit': { icon: 'chart-bar', tone: 'info' },
};

const DEFAULT_STYLE = { icon: 'chart-bar', tone: 'info' } as const;

/**
 * WHICH CARD WEARS THE RING.
 *
 * The reference rings exactly one of its four: "Invoices by Status", whose
 * whole subject is the split. The other three carry a figure and a sparkline
 * even where the metric behind them is dimensioned — "Bills Awaiting Approval"
 * is a count with a trend, not a pie — and ringing them too made a 190px row
 * where the reference draws 130 and buried the figure under three keys nobody
 * asked for.
 *
 * A SET RATHER THAN A MAP, because the word the reference sets under the ring's
 * total — `<span class="text-[8px] text-slate-400">Invoices</span>` — is not
 * drawn: at any size the frozen scale sets type at, it is wider than the space
 * inside a 72-pixel ring and crosses the arc. The card's label says what is
 * being counted, one line above.
 */
const RINGED: ReadonlySet<string> = new Set(['finance.invoices_by_status']);

function styleFor(key: string): { readonly icon: IconName; readonly tone: BadgeTone } {
  return METRIC_STYLE[key] ?? DEFAULT_STYLE;
}

/** Paid really is green and overdue really is red; the rest carry no hue. */
const SEGMENT_TONE: Record<string, BadgeTone> = {
  paid: 'success',
  settled: 'success',
  approved: 'success',
  issued: 'info',
  sent: 'info',
  submitted: 'info',
  overdue: 'danger',
  disputed: 'danger',
  rejected: 'danger',
  awaiting_approval: 'warning',
  draft: 'neutral',
  void: 'neutral',
};

/**
 * "GBP – British Pound", from the ISO 4217 register the runtime already holds.
 *
 * `Intl.DisplayNames` reads CLDR, which carries the ISO currency names, so the
 * reference's longer label costs no endpoint, no table of names to maintain and
 * no claim this file invented. The code is still what the entity stores and
 * still what is shown: the name is added to it, never in place of it.
 *
 * FALLS BACK TO THE CODE ALONE, on three counts: a runtime without
 * `DisplayNames`, one that throws on a malformed code, and one that answers
 * with the code itself because it has no name for it. A currency the register
 * does not name reads as its code, which is what the column holds anyway.
 */
function currencyName(code: string): string {
  try {
    const name = new Intl.DisplayNames(['en-GB'], { type: 'currency' }).of(code);
    return name === undefined || name === code ? code : `${code} – ${name}`;
  } catch {
    return code;
  }
}

const NUMBER = new Intl.NumberFormat('en-GB');
const MONEY = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2 });

/**
 * A FIGURE IN THE UNIT THE SERVER DECLARED IT IN.
 *
 * `currency` means MINOR UNITS — the ledger is `numeric(38, 0)` in pence for
 * the reason floats are not money — so it is divided here and only here. No
 * symbol, because a metric card carries no currency code: a tenant may hold
 * entities in several, and the meta strip above states the base currency of the
 * books these figures are from.
 */
function cardValue(card: MetricCard): string | undefined {
  if (card.unavailable !== undefined || card.total === null) return undefined;
  return card.unit === 'currency' ? MONEY.format(card.total / 100) : NUMBER.format(card.total);
}

/**
 * The delta beside each headline figure.
 *
 * THE ONE ARITHMETIC THIS SCREEN DOES, and it states no new fact: `total` and
 * `previousTotal` are both the platform's, both already scoped to this caller,
 * so the percentage presents one answer rather than offering a second.
 */
function changePercent(card: MetricCard): number | undefined {
  const { total, previousTotal } = card;
  if (total === null || previousTotal === null || previousTotal === 0) return undefined;
  return ((total - previousTotal) / Math.abs(previousTotal)) * 100;
}

/**
 * The sparkline's bars: one per period the server returned, in order.
 *
 * SUMMED ACROSS DIMENSIONS, NOT ACROSS PERIODS. A dimensioned metric carries
 * one point per period AND status, and a bar per point would draw the same
 * month three times. With a single period there is nothing to trend, so the
 * comparison the delta is a percentage of is plotted instead — two figures the
 * server measured for two windows.
 */
function trendPoints(card: MetricCard): readonly number[] | undefined {
  const byPeriod = new Map<string, number>();
  for (const point of card.points) {
    byPeriod.set(point.periodStart, (byPeriod.get(point.periodStart) ?? 0) + point.value);
  }
  if (byPeriod.size > 1) {
    return [...byPeriod].sort(([a], [b]) => a.localeCompare(b)).map(([, value]) => value);
  }
  if (card.previousTotal !== null && card.total !== null) return [card.previousTotal, card.total];
  return undefined;
}

/**
 * The donut's segments, from the figures the server already sent.
 *
 * A DIMENSIONED METRIC IS ALREADY A BREAKDOWN — `finance.invoices_by_status`
 * carries one point per status — so the ring is a drawing of the server's own
 * answer rather than a count this screen did.
 */
function segmentsOf(card: MetricCard): readonly RingSegment[] {
  const byDimension = new Map<string, number>();
  for (const point of card.points) {
    if (point.dimensionKey === '') continue;
    byDimension.set(point.dimensionKey, (byDimension.get(point.dimensionKey) ?? 0) + point.value);
  }
  return [...byDimension]
    .filter(([, value]) => value > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([dimensionKey, value]) => ({
      label: dimensionKey.charAt(0).toUpperCase() + dimensionKey.slice(1).replaceAll('_', ' '),
      value,
      tone: SEGMENT_TONE[dimensionKey.toLowerCase()] ?? 'neutral',
    }));
}

/**
 * "vs. previous period", where the designer writes "vs. previous month".
 *
 * `/metrics` compares a window against the same number of days immediately
 * before it and does not say which days those were. Naming the month would be
 * a claim about the window that the payload does not support, and Screen 00
 * only names it because the dashboard endpoint sends the window it used.
 */
const COMPARISON_CAPTION = 'vs. previous period';

/**
 * One KPI card, in whichever of the designer's two shapes the data has.
 *
 * THE DESIGNER'S CARD, FROM THE DESIGNER MODULE. Screen 01's band was drawn
 * from the same Stitch reference their page descends from, so it already had
 * this anatomy; what it did not have was the same COMPONENT as Screens 04, 08
 * and 10, and four Finance screens drawing the designer's card two different
 * ways is the integration failing at its one job. The figures, the refusal
 * shape and the four-card composition are unchanged.
 */
function Kpi({ card }: { readonly card: MetricCard }) {
  const { icon, tone } = styleFor(card.key);
  const value = cardValue(card);
  const segments = card.unavailable === undefined ? segmentsOf(card) : [];
  const change = changePercent(card);

  /*
   * THE RING IS THE DESIGNER'S SECOND CARD AND NOTHING ELSE — and it still
   * needs a breakdown to draw: a ring of one colour is a circle.
   */
  if (RINGED.has(card.key) && segments.length > 1) {
    return (
      <MetricRing
        icon={icon}
        tone={tone}
        label={card.title}
        total={value ?? null}
        segments={segments}
      />
    );
  }

  const points = card.unavailable === undefined ? trendPoints(card) : undefined;
  return (
    <DesignerMetricCard
      icon={icon}
      tone={tone}
      label={card.title}
      value={value ?? null}
      {...(change === undefined
        ? {}
        : {
            /*
              THE DESIGNER DRAWS A SIGNED PERCENTAGE AND AN ARROW. The sign is
              the arrow's direction and the figure is its magnitude, which is
              how their card reads; the comparison is the platform's own
              `previousTotal`, so nothing new is claimed by presenting it.
            */
            change: `${String(Math.abs(Math.round(change)))}%`,
            changeDirection: change < 0 ? ('down' as const) : ('up' as const),
            caption: COMPARISON_CAPTION,
          })}
      {...(points === undefined ? {} : { points })}
      {...(card.unavailable === undefined ? {} : { unavailable: card.unavailable.detail })}
    />
  );
}

/**
 * WHOSE BOOKS, IN WHAT CURRENCY, ON WHAT BASIS, AND ARE THEY OPEN.
 *
 * Four facts a person needs before they read a single figure on this page. It
 * was a `<dl>` inside one card, which is correct markup and the wrong
 * composition: the four read as one paragraph, and the mark that tells you at a
 * glance which is which had nowhere to go. The reference gives each its own
 * card, its own tinted mark and its own label, and so does this.
 *
 * EVERY VALUE IS THE SERVER'S OWN, rendered. Nothing here decides whether the
 * books are open — `lifecycle` is a column and `wentLiveAt` is a timestamp.
 */
function ContextStrip({ settings }: { readonly settings: EntitySettings }) {
  const live = settings.lifecycle === 'live';
  return (
    <MetaStrip>
      <MetaCard
        icon="bank"
        tone="info"
        label="Base currency"
        value={currencyName(settings.baseCurrency)}
      />
      <MetaCard
        icon="chart-bar"
        tone="accent"
        label="Accounting basis"
        value={settings.accountingBasis.replace(/_/g, ' ')}
      />
      <MetaCard
        icon="diamond"
        tone="warning"
        label="VAT scheme"
        value={settings.vatScheme.replace(/_/g, ' ')}
      />
      {/*
       * "BOOKS LIVE SINCE / 01 Jan 2024" IS THE REFERENCE'S FOURTH CARD, and it
       * is only true of books that are live. An entity still in setup gets the
       * lifecycle itself, as a chip, because a date under "Books live since" for
       * books that are not live would be a claim nothing supports — and the
       * whole point of this strip is that a figure below it can be trusted.
       */}
      {live && settings.wentLiveAt !== null ? (
        <MetaCard
          icon="calendar-blank"
          tone="success"
          label="Books live since"
          value={formatDay(settings.wentLiveAt.slice(0, 10))}
        />
      ) : (
        <MetaCard
          icon={live ? 'check-circle' : 'warning-circle'}
          tone={live ? 'success' : 'warning'}
          label="Books"
          value={
            <StatusChip tone={live ? 'success' : 'warning'}>
              {settings.lifecycle.replace(/_/g, ' ')}
            </StatusChip>
          }
        />
      )}
    </MetaStrip>
  );
}

export function FinanceOverviewScreen({
  finance,
  analytics,
  parties,
  legalEntityId,
  actions,
  navigation,
  onViewInvoices,
  onViewBills,
}: {
  readonly finance: FinanceGateway;
  readonly analytics: AnalyticsGateway;
  /**
   * WHO THE INVOICE IS TO AND WHO THE BILL IS FROM.
   *
   * The reference's records tables carry a "Customer" and a "Supplier" column,
   * and an invoice carries a `partyId` rather than a name. This reads the names
   * from the register the product already exposes, under the permission that
   * register already guards on — and when the caller may not read it, or the
   * screen is given no register at all, the column falls back to the document's
   * own reference rather than a name assembled from nothing.
   */
  readonly parties?: PartiesGateway | undefined;
  readonly legalEntityId?: string | undefined;
  /**
   * The two or three things somebody opens this page to START.
   *
   * Passed in rather than built here, because what a person may do is a
   * permission question and the answer is the router's — this screen renders
   * what it is given and decides nothing about who may see it.
   */
  readonly actions?: ReactNode;
  /** Where to go from here. The module's own links, in their groups. */
  readonly navigation?: ReactNode;
  /**
   * The reference's "View all →" on each records card. Absent where the caller
   * may not open the list, for the same reason every other link here is: a
   * control leading to a 403 is worse than no control.
   */
  readonly onViewInvoices?: (() => void) | undefined;
  readonly onViewBills?: (() => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    metrics: readonly MetricCard[];
    invoices: readonly Invoice[];
    bills: readonly Bill[];
    entities: readonly EntitySettings[];
    names: ReadonlyMap<string, string>;
  }>(
    async () => ({
      metrics: await analytics.metrics(),
      invoices: await finance.invoices(legalEntityId),
      bills: await finance.bills(legalEntityId),
      // Every entity within reach; the strip shows the one this page is about.
      // A failure here must not take the whole page down — the figures below
      // are still true without it.
      entities: await finance.entitySettings().catch(() => []),
      // Same trade for the party register: a refusal costs the two name
      // columns, not the page.
      names:
        parties === undefined
          ? new Map<string, string>()
          : await parties
              .parties({})
              .then((all) => new Map(all.map((one) => [one.id, one.displayName])))
              .catch(() => new Map<string, string>()),
    }),
    [finance, analytics, parties, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading Finance…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { metrics, invoices, bills, entities, names } = state.value;
  const settings = entities.find((entity) => entity.legalEntityId === legalEntityId);

  /*
   * THE FOUR THE REFERENCE DRAWS, IN ITS ORDER, AND NO OTHERS.
   *
   * Still the server's cards, so a tenant without Finance has none of them and
   * a caller refused a figure gets the refusal rather than a number — the
   * filter decides COMPOSITION, which is the markup's business, and decides
   * nothing about authorisation, which is the server's.
   */
  const financeCards = metrics
    .filter((metric) => rank(metric.key) !== -1)
    .slice()
    .sort((a, b) => rank(a.key) - rank(b.key));

  return (
    <>
      {/*
       * THE SHELL NAMES THIS PAGE, as it names every other one: the breadcrumb,
       * the heading and the subtitle all come from the route table, in the top
       * header. What is left here is the half of the reference's banner that is
       * this screen's own — the two things it exists to start — and the content
       * begins with them.
       *
       * IT WAS DRAWN HERE FOR ONE PASS and that was the wrong trade: Screen 01
       * gained the reference's compact band and the product lost a rule, so the
       * dashboard named itself in the header and Finance named itself twice, in
       * two different places. One authority for page identity is worth more
       * than one screen's 56-pixel header, and a module added tomorrow inherits
       * it by adding a row to the same table.
       */}
      <PageHeader {...(actions === undefined ? {} : { actions })} />

      {/* Absent when the entity is not known yet, rather than four dashes. */}
      {settings !== undefined && <ContextStrip settings={settings} />}

      {financeCards.length === 0 ? (
        <EmptyState
          icon="chart-bar"
          title="No Finance figures for your access"
          description="Metrics appear here once there is something within your reach to count."
        />
      ) : (
        /*
         * FOUR COLUMNS, STATED. The designer sizes this strip
         * `repeat(auto-fit, minmax(260px, 1fr))`, which is two rows of two the
         * moment the shell's content column is under 1064px — and at 1280 it
         * is. §15: the count is exact.
         */
        <MetricBand>
          {financeCards.map((card) => (
            <Kpi key={card.key} card={card} />
          ))}
        </MetricBand>
      )}

      {navigation}

      <RecordRow>
        <RecordCard
          icon="tray"
          tone="success"
          title="Recent invoices"
          {...(onViewInvoices === undefined
            ? {}
            : { actionLabel: 'View all', onAction: onViewInvoices })}
        >
          {invoices.length === 0 ? (
            <EmptyState compact icon="bank" title="No invoices" description="Within your reach." />
          ) : (
            <Table label="Recent invoices">
              <thead>
                <Tr>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th>Issued</Th>
                  <Th>Due</Th>
                  <Th align="right">Gross</Th>
                  <Th>Status</Th>
                </Tr>
              </thead>
              <tbody>
                {invoices.slice(0, 5).map((invoice) => (
                  <Tr key={invoice.id}>
                    <Td>{invoice.invoiceNumber ?? 'Draft'}</Td>
                    <Td variant="secondary">
                      {names.get(invoice.partyId) ?? invoice.customerReference ?? '—'}
                    </Td>
                    <Td variant="secondary">
                      {invoice.issueDate === null ? '—' : formatDay(invoice.issueDate)}
                    </Td>
                    <Td variant="secondary">
                      {invoice.dueDate === null ? '—' : formatDay(invoice.dueDate)}
                    </Td>
                    <Td variant="money">{formatMoney(invoice.grossTotal, invoice.currency)}</Td>
                    <Td className="finance-record-status">
                      {/*
                        THE SERVER'S WORD, WITHOUT THE COLUMN NAME IN IT. This
                        card showed `part_paid` and `pending_approval` verbatim
                        — §18's "internal technical wording in customer-facing
                        UI" — and it is the same chip Screen 04 draws, from the
                        same table, so the two pages cannot disagree.
                      */}
                      <DesignerBadge tone={invoiceStatusTone(invoice.status)}>
                        {invoiceStatusWord(invoice.status)}
                      </DesignerBadge>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </RecordCard>

        <RecordCard
          icon="arrows-down-up"
          tone="danger"
          title="Recent bills"
          {...(onViewBills === undefined ? {} : { actionLabel: 'View all', onAction: onViewBills })}
        >
          {bills.length === 0 ? (
            <EmptyState compact icon="bank" title="No bills" description="Within your reach." />
          ) : (
            <Table label="Recent bills">
              <thead>
                <Tr>
                  <Th>Reference</Th>
                  <Th>Supplier</Th>
                  <Th>Bill date</Th>
                  <Th>Due</Th>
                  <Th align="right">Gross</Th>
                  <Th>Status</Th>
                </Tr>
              </thead>
              <tbody>
                {bills.slice(0, 5).map((bill) => (
                  <Tr key={bill.id}>
                    <Td>{bill.supplierReference}</Td>
                    <Td variant="secondary">{names.get(bill.partyId) ?? '—'}</Td>
                    <Td variant="secondary">{formatDay(bill.billDate)}</Td>
                    <Td variant="secondary">
                      {bill.dueDate === null ? '—' : formatDay(bill.dueDate)}
                    </Td>
                    <Td variant="money">{formatMoney(bill.grossTotal, bill.currency)}</Td>
                    <Td className="finance-record-status">
                      <DesignerBadge tone={billStatusTone(bill.status)}>
                        {billStatusWord(bill.status)}
                      </DesignerBadge>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </RecordCard>
      </RecordRow>
    </>
  );
}

/** Where a card sits. Only the four above have a place on this screen. */
function rank(key: string): number {
  return SCREEN_CARDS.indexOf(key);
}
